import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdSave, MdRefresh, MdAdd, MdDelete, MdAttachMoney, MdStars, MdExpandMore, MdExpandLess } from 'react-icons/md';

function AdminPollPricing({ category = 'polls' }) {
  const isAssessments = category === 'assessments';
  const matchesCategory = (pt) => (
    isAssessments
      ? pt.id.startsWith('candidacy_')
      : !pt.id.startsWith('candidacy_')
  );

  const [pollTypes, setPollTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState(null);
  const [expandedTypes, setExpandedTypes] = useState({});

  const loadPricing = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/poll-pricing`);
      setPollTypes(res.data.poll_types);
      // Expand only items in this view by default
      const expanded = {};
      res.data.poll_types.forEach(pt => { expanded[pt.id] = matchesCategory(pt); });
      setExpandedTypes(expanded);
      setHasChanges(false);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load pricing' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssessments]);

  useEffect(() => { loadPricing(); }, [loadPricing]);

  const handleSave = async () => {
    for (const pt of pollTypes) {
      if (!pt.name?.trim()) {
        setMessage({ type: 'error', text: 'All poll types must have a name' });
        return;
      }
      for (const d of pt.durations) {
        if (!pt.id.startsWith('candidacy_') && (!d.days || d.days < 1)) {
          setMessage({ type: 'error', text: `Invalid duration in "${pt.name}"` });
          return;
        }
        if (d.price_dollars < 0 || d.price_points < 0) {
          setMessage({ type: 'error', text: `Prices cannot be negative in "${pt.name}"` });
          return;
        }
      }
    }

    try {
      setSaving(true);
      const res = await axios.put(`${API_URL}/api/admin/poll-pricing`, { poll_types: pollTypes });
      setPollTypes(res.data.poll_types);
      setHasChanges(false);
      setMessage({ type: 'success', text: 'Pricing saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save pricing' });
    } finally {
      setSaving(false);
    }
  };

  const updatePollType = (typeIndex, field, value) => {
    const updated = [...pollTypes];
    updated[typeIndex] = { ...updated[typeIndex], [field]: value };
    setPollTypes(updated);
    setHasChanges(true);
  };

  const updateDuration = (typeIndex, durIndex, field, value) => {
    const updated = [...pollTypes];
    const durations = [...updated[typeIndex].durations];
    durations[durIndex] = {
      ...durations[durIndex],
      [field]: field === 'label' ? value : Number(value)
    };
    updated[typeIndex] = { ...updated[typeIndex], durations };
    setPollTypes(updated);
    setHasChanges(true);
  };

  const addDuration = (typeIndex) => {
    const updated = [...pollTypes];
    const durations = [...updated[typeIndex].durations];
    const maxDays = durations.length > 0 ? Math.max(...durations.map(d => d.days)) : 0;
    durations.push({ days: maxDays + 7, label: `${maxDays + 7} Days`, price_dollars: 0, price_points: 0 });
    updated[typeIndex] = { ...updated[typeIndex], durations };
    setPollTypes(updated);
    setHasChanges(true);
  };

  const removeDuration = (typeIndex, durIndex) => {
    const updated = [...pollTypes];
    const durations = [...updated[typeIndex].durations];
    if (durations.length <= 1) {
      setMessage({ type: 'error', text: 'At least one duration is required per poll type' });
      return;
    }
    durations.splice(durIndex, 1);
    updated[typeIndex] = { ...updated[typeIndex], durations };
    setPollTypes(updated);
    setHasChanges(true);
  };

  const addPollType = () => {
    const newId = `poll_type_${Date.now()}`;
    setPollTypes([...pollTypes, {
      id: newId,
      name: '',
      description: '',
      enabled: false,
      durations: [{ days: 1, label: '1 Day', price_dollars: 0, price_points: 0 }]
    }]);
    setExpandedTypes(prev => ({ ...prev, [newId]: true }));
    setHasChanges(true);
  };

  const removePollType = (typeIndex) => {
    if (pollTypes.length <= 1) {
      setMessage({ type: 'error', text: 'At least one poll type is required' });
      return;
    }
    setPollTypes(pollTypes.filter((_, i) => i !== typeIndex));
    setHasChanges(true);
  };

  const toggleExpanded = (id) => {
    setExpandedTypes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center text-gray-500 py-12 font-rubik">Loading pricing...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-rubik" data-testid="admin-poll-pricing-title">
            {isAssessments ? 'Assessment Pricing' : 'Poll Pricing'}
          </h1>
          <p className="text-sm text-gray-500 font-rubik mt-1">
            {isAssessments
              ? 'Configure pricing for candidacy assessments (point-in-time reports) in dollars and points'
              : 'Configure pricing for each poll type in dollars and points'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadPricing}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 text-sm font-rubik"
            data-testid="reset-pricing-btn"
          >
            <MdRefresh /> Reset
          </button>
          {!isAssessments && (
            <button
              onClick={addPollType}
              className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors flex items-center gap-2 text-sm font-rubik"
              data-testid="add-poll-type-btn"
            >
              <MdAdd /> Add Poll Type
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`px-5 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-rubik font-semibold ${
              hasChanges ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="save-pricing-btn"
          >
            <MdSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-sm font-rubik ${
            message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}
          data-testid="pricing-message"
        >
          {message.text}
        </div>
      )}

      <div className="space-y-4">
        {pollTypes.filter(matchesCategory).map((pt) => {
          const typeIndex = pollTypes.findIndex(p => p.id === pt.id);
          return (
          <div
            key={pt.id}
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            data-testid={`poll-type-${pt.id}`}
          >
            {/* Poll Type Header */}
            <div
              className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleExpanded(pt.id)}
            >
              <div className="flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full ${pt.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 font-rubik">{pt.name || 'New Poll Type'}</h3>
                  <p className="text-sm text-gray-500 font-rubik">{pt.description || 'No description'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-2 py-1 rounded text-xs font-rubik font-medium ${pt.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {pt.enabled ? 'Active' : 'Inactive'}
                </span>
                {!pt.id.startsWith('candidacy_') && (
                  <span className="text-xs text-gray-400 font-rubik">{pt.durations.length} duration{pt.durations.length !== 1 ? 's' : ''}</span>
                )}
                {expandedTypes[pt.id] ? <MdExpandLess className="text-gray-400 text-xl" /> : <MdExpandMore className="text-gray-400 text-xl" />}
              </div>
            </div>

            {/* Expanded Content */}
            {expandedTypes[pt.id] && (
              <div className="border-t border-gray-100 p-5 space-y-5">
                {/* Poll Type Settings */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 font-rubik block mb-1">Name</label>
                    <input
                      type="text"
                      value={pt.name}
                      onChange={(e) => updatePollType(typeIndex, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                      placeholder="Poll type name"
                      data-testid={`poll-type-name-${typeIndex}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-rubik block mb-1">Description</label>
                    <input
                      type="text"
                      value={pt.description}
                      onChange={(e) => updatePollType(typeIndex, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                      placeholder="Short description"
                      data-testid={`poll-type-desc-${typeIndex}`}
                    />
                  </div>
                  <div className="flex items-end gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pt.enabled}
                        onChange={(e) => updatePollType(typeIndex, 'enabled', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        data-testid={`poll-type-enabled-${typeIndex}`}
                      />
                      <span className="text-sm font-rubik text-gray-700">Enabled</span>
                    </label>
                    <button
                      onClick={() => removePollType(typeIndex)}
                      className="ml-auto px-3 py-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors text-sm font-rubik flex items-center gap-1"
                      data-testid={`remove-poll-type-${typeIndex}`}
                    >
                      <MdDelete size={16} /> Remove
                    </button>
                  </div>
                </div>

                {/* Spotlight Cooldown Setting */}
                {pt.id === 'spotlight_poll' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <label className="text-xs text-amber-700 font-rubik font-semibold block mb-1">
                        Cooldown Between Spotlight Polls (Hours)
                      </label>
                      <p className="text-xs text-amber-600 font-rubik mb-2">
                        After answering a spotlight poll, users won't see another one for this many hours.
                      </p>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={pt.cooldown_hours ?? 6}
                        onChange={(e) => updatePollType(typeIndex, 'cooldown_hours', Number(e.target.value))}
                        className="w-32 px-3 py-2 border border-amber-300 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-amber-200 focus:border-amber-400 outline-none"
                        data-testid="spotlight-cooldown-hours"
                      />
                    </div>
                    <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <label className="text-xs text-indigo-700 font-rubik font-semibold block mb-1">
                        Surge Price Scale (Hours)
                      </label>
                      <p className="text-xs text-indigo-600 font-rubik mb-2">
                        Controls how fast prices rise with demand. Every X hours of estimated wait adds 1x to the base price multiplier. Lower = steeper price curve.
                      </p>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={pt.surge_scale_hours ?? 12}
                        onChange={(e) => updatePollType(typeIndex, 'surge_scale_hours', Number(e.target.value))}
                        className="w-32 px-3 py-2 border border-indigo-300 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                        data-testid="spotlight-surge-scale-hours"
                      />
                      <p className="text-xs text-indigo-400 font-rubik mt-1.5">
                        Example: With cooldown={pt.cooldown_hours ?? 6}h and scale={pt.surge_scale_hours ?? 12}h, a queue of 4 polls = {((pt.cooldown_hours ?? 6) * 4)}h wait = {(1 + ((pt.cooldown_hours ?? 6) * 4) / (pt.surge_scale_hours ?? 12)).toFixed(1)}x multiplier
                      </p>
                    </div>
                  </div>
                )}

                {/* Pricing Section */}
                {pt.id.startsWith('candidacy_') ? (
                  /* Simplified pricing for candidacy assessments (point-in-time reports) */
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 font-rubik mb-3">Pricing</h4>
                    <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-6">
                      <div>
                        <label className="text-xs text-gray-400 font-rubik block mb-1">Price (USD)</label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={pt.durations[0]?.price_dollars ?? 0}
                            onChange={(e) => updateDuration(typeIndex, 0, 'price_dollars', e.target.value)}
                            className="w-32 pl-6 pr-2 py-1.5 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                            data-testid={`candidacy-price-${typeIndex}`}
                          />
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 font-rubik pt-4">
                        Point-in-time snapshot report
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Full duration pricing table for regular poll types */
                  <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-600 font-rubik">Duration Pricing</h4>
                    <button
                      onClick={() => addDuration(typeIndex)}
                      className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors flex items-center gap-1 text-xs font-rubik"
                      data-testid={`add-duration-${typeIndex}`}
                    >
                      <MdAdd size={14} /> Add Duration
                    </button>
                  </div>

                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-3 mb-2 px-3">
                    <div className="col-span-2 text-xs text-gray-400 font-rubik font-medium">Days</div>
                    <div className="col-span-3 text-xs text-gray-400 font-rubik font-medium">Label</div>
                    <div className="col-span-3 text-xs text-gray-400 font-rubik font-medium flex items-center gap-1">
                      <MdAttachMoney size={12} /> Price (USD)
                    </div>
                    <div className="col-span-3 text-xs text-gray-400 font-rubik font-medium flex items-center gap-1">
                      <MdStars size={12} /> Price (Points)
                    </div>
                    <div className="col-span-1"></div>
                  </div>

                  {/* Duration Rows */}
                  <div className="space-y-2">
                    {pt.durations.map((dur, durIndex) => (
                      <div
                        key={durIndex}
                        className="grid grid-cols-12 gap-3 items-center bg-gray-50 rounded-lg p-3"
                        data-testid={`duration-row-${typeIndex}-${durIndex}`}
                      >
                        <div className="col-span-2">
                          <input
                            type="number"
                            min="1"
                            value={dur.days}
                            onChange={(e) => updateDuration(typeIndex, durIndex, 'days', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                            data-testid={`dur-days-${typeIndex}-${durIndex}`}
                          />
                        </div>
                        <div className="col-span-3">
                          <input
                            type="text"
                            value={dur.label}
                            onChange={(e) => updateDuration(typeIndex, durIndex, 'label', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                            data-testid={`dur-label-${typeIndex}-${durIndex}`}
                          />
                        </div>
                        <div className="col-span-3">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={dur.price_dollars}
                              onChange={(e) => updateDuration(typeIndex, durIndex, 'price_dollars', e.target.value)}
                              className="w-full pl-6 pr-2 py-1.5 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                              data-testid={`dur-price-${typeIndex}-${durIndex}`}
                            />
                          </div>
                        </div>
                        <div className="col-span-3">
                          <input
                            type="number"
                            min="0"
                            value={dur.price_points}
                            onChange={(e) => updateDuration(typeIndex, durIndex, 'price_points', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                            data-testid={`dur-points-${typeIndex}-${durIndex}`}
                          />
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <button
                            onClick={() => removeDuration(typeIndex, durIndex)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-500 transition-colors"
                            title="Remove duration"
                            data-testid={`dur-delete-${typeIndex}-${durIndex}`}
                          >
                            <MdDelete size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>

      {pollTypes.filter(matchesCategory).length === 0 && (
        <div className="text-center py-12 text-gray-400 font-rubik">
          {isAssessments
            ? 'No candidacy assessment types configured.'
            : 'No poll types configured. Click "Add Poll Type" to create one.'}
        </div>
      )}
    </div>
  );
}

export default AdminPollPricing;
